import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Box, Card, CardContent, Chip, IconButton, Stack, Table, TableBody, TableCell, TableContainer, TableHead,
  TablePagination, TableRow, Tooltip, Typography,
} from '@mui/material'
import { useForm } from 'react-hook-form'
import _ from 'lodash'
import RefreshIcon from '@mui/icons-material/Refresh'
import AcUnitOutlinedIcon from '@mui/icons-material/AcUnitOutlined'
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined'
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined'
import PageTitle from '../../components/PageTitle'
import Loader from '../../common/Loader'
import { FieldLabel, FilterField } from '../../common/FilterBar'
import { FormProvider, RHFSearchableSelectField, RHFSelectField, RHFTextField } from '../../common/hook-form'
import { useGetAllActiveMerchantsIdAndNameCachedQuery } from '../../redux/commonApiCalls'
import useTabParam from '../../hooks/useTabParam'
import PillFilters from '../AppointmentDeliveriesV2/shared/PillFilters'
import PageBanner from '../AppointmentDeliveriesV2/shared/PageBanner'
import { wfList, wfCounts } from './api'
import { fmtKg, fmtDate, ageLabel, WF_TABS, WF_MAX_SUBMISSIONS, WF_LEVEL_LABEL, wfTabCounts } from './format'
import { WfStatusChip, LevelChip } from './shared/Chips'
import FreezeRequestDrawer from './FreezeRequestDrawer'

const LEVEL_OPTIONS = [{ value: '', label: 'Any level' }].concat(
  Object.keys(WF_LEVEL_LABEL).map((k) => ({ value: k, label: WF_LEVEL_LABEL[k] }))
)
const PAGE_SIZES = [10, 25, 50, 100]

const headSx = { fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.4px', color: '#888', fontWeight: 700, whiteSpace: 'nowrap', py: 1 }
const cellSx = { fontSize: 12, py: 1, verticalAlign: 'top' }
const muted = { fontSize: 11, color: '#6b7280' }

/**
 * Merchant Weight Freeze requests across every merchant, defaulting to the open
 * ones. The list endpoint has no total count (open-ended paging: limit+1 tells
 * us whether a next page exists), the stat pills come from /counts.
 */
export default function FreezeRequestsPage() {
  const [tab, setTab] = useTabParam('open')
  const [filters, setFilters] = useState({})
  const [page, setPage] = useState(0)
  const [size, setSize] = useState(25)
  const [rows, setRows] = useState([])
  const [hasMore, setHasMore] = useState(false)
  const [counts, setCounts] = useState({})
  const [loading, setLoading] = useState(false)
  const [reload, setReload] = useState(0)
  const [openReq, setOpenReq] = useState(null)

  const methods = useForm({ defaultValues: { merchantId: '', level: '', q: '' } })
  const { handleSubmit, reset } = methods
  const { data: allMerchants = [] } = useGetAllActiveMerchantsIdAndNameCachedQuery()
  const merchantOptions = useMemo(() => _.orderBy(allMerchants, 'name').map((m) => ({ value: m.id, label: m.name })), [allMerchants])

  useEffect(() => {
    let alive = true
    setLoading(true)
    wfList({
      merchantId: filters.merchantId,
      status: tab === 'all' ? undefined : tab,
      level: filters.level,
      q: filters.q,
      limit: size + 1,
      skip: page * size,
    })
      .then((list) => {
        if (!alive) return
        const arr = Array.isArray(list) ? list : []
        setHasMore(arr.length > size)
        setRows(arr.slice(0, size))
      })
      .catch((err) => { if (alive) { console.error(err); setRows([]); setHasMore(false) } })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [tab, filters, page, size, reload])

  useEffect(() => {
    let alive = true
    wfCounts({ merchantId: filters.merchantId })
      .then((c) => { if (alive) setCounts(c || {}) })
      .catch(() => { if (alive) setCounts({}) })
    return () => { alive = false }
  }, [filters.merchantId, reload])

  const onSubmit = (v) => { setFilters({ merchantId: v.merchantId || '', level: v.level || '', q: (v.q || '').trim() }); setPage(0) }
  const onReset = () => { reset({ merchantId: '', level: '', q: '' }); setFilters({}); setPage(0) }
  const changeTab = (k) => { setTab(k); setPage(0) }

  const onChanged = useCallback((updated) => {
    setReload((n) => n + 1)
    setOpenReq(updated || null)
  }, [])

  const tabCounts = useMemo(() => wfTabCounts(counts), [counts])
  // Open-ended paging: the count is "what we know so far" so the pager can offer a next page.
  const knownCount = page * size + rows.length + (hasMore ? 1 : 0)

  return (
    <>
      <PageTitle>Weight v2 — Merchant Freeze Requests</PageTitle>
      {loading && <Loader />}

      <PageBanner variant='info' icon={<AcUnitOutlinedIcon />} title='Merchant weight freeze requests waiting for a decision'>
        A merchant proposes one chargeable weight for a SKU, sub-category or combination, against a declared packing material.
        Ask for more data (the merchant has {WF_MAX_SUBMISSIONS} submissions in all), approve, or reject with a reason.
        Approval records the agreement and supersedes any earlier one for the same subject; it does not change the merchant's bill until billing on merchant freezes is switched on.
      </PageBanner>

      <Card sx={{ mb: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.05)', borderRadius: '8px' }}>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-end' }}>
              <FilterField>
                <FieldLabel>Merchant</FieldLabel>
                <RHFSearchableSelectField name='merchantId' label='' options={merchantOptions} fullWidth />
              </FilterField>
              <FilterField>
                <FieldLabel>Level</FieldLabel>
                <RHFSelectField name='level' label='' options={LEVEL_OPTIONS} width='100%' size='small' displayEmpty />
              </FilterField>
              <FilterField>
                <FieldLabel>Reference / subject</FieldLabel>
                <RHFTextField name='q' label='' placeholder='WF-… or SKU code' fullWidth size='small' InputLabelProps={{ shrink: true }} />
              </FilterField>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flex: '0 0 auto' }}>
                <Box component='button' type='submit'
                  sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', px: '20px', py: '8px', borderRadius: '6px', border: 'none', cursor: 'pointer', bgcolor: '#1565c0', color: '#fff', '&:hover': { bgcolor: '#0d47a1' } }}>
                  Filter
                </Box>
                <Box component='button' type='button' onClick={onReset}
                  sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', px: '20px', py: '8px', borderRadius: '6px', border: 'none', cursor: 'pointer', bgcolor: '#ef4444', color: '#fff', '&:hover': { bgcolor: '#dc2626' } }}>
                  Reset
                </Box>
              </Box>
            </Box>
          </FormProvider>
        </CardContent>
      </Card>

      <Stack direction='row' alignItems='center' justifyContent='space-between' flexWrap='wrap' rowGap={1} sx={{ mb: 0.5 }}>
        <PillFilters pills={WF_TABS} value={tab} onChange={changeTab} counts={tabCounts} />
        <Tooltip title='Reload'><IconButton size='small' onClick={() => setReload((n) => n + 1)} sx={{ mb: 2 }}><RefreshIcon fontSize='small' /></IconButton></Tooltip>
      </Stack>

      <Card sx={{ boxShadow: '0 1px 3px rgba(0,0,0,0.05)', borderRadius: '8px' }}>
        <TableContainer>
          <Table size='small' sx={{ minWidth: 1000 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={headSx}>Request · merchant</TableCell>
                <TableCell sx={headSx}>Subject</TableCell>
                <TableCell sx={headSx} align='right'>Proposed</TableCell>
                <TableCell sx={headSx}>Packing</TableCell>
                <TableCell sx={headSx} align='center'>Evidence</TableCell>
                <TableCell sx={headSx} align='center'>Submissions</TableCell>
                <TableCell sx={headSx}>Status</TableCell>
                <TableCell sx={headSx}>Raised</TableCell>
                <TableCell sx={headSx} />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => {
                const ev = r.evidence || {}
                const images = (ev.images || []).length
                const dws = (ev.dwsAwbs || []).length
                return (
                  <TableRow key={r.id} hover sx={{ cursor: 'pointer' }} onClick={() => setOpenReq(r)}>
                    <TableCell sx={cellSx}>
                      <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{r.requestRef || r.id}</Typography>
                      <Typography sx={muted}>{r.merchantName || r.merchantId}</Typography>
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <Stack direction='row' spacing={0.75} alignItems='center'>
                        <LevelChip level={r.level} />
                        <Typography sx={{ fontSize: 12, fontWeight: 600 }} title={r.subject}>{r.subject}</Typography>
                      </Stack>
                      {(r.covers || []).length > 1 && <Typography sx={muted}>covers {r.covers.length} SKUs</Typography>}
                    </TableCell>
                    <TableCell sx={cellSx} align='right'>
                      <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{fmtKg(r.proposedChargeKg)}</Typography>
                      <Typography sx={muted}>dead {fmtKg(r.proposedDeadKg)} · vol {fmtKg(r.proposedVolKg)}</Typography>
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <Typography sx={{ fontSize: 12 }}>{r.packLabel || r.packId || '—'}</Typography>
                      {r.packSelfWeightG != null && <Typography sx={muted}>{r.packSelfWeightG} g self weight</Typography>}
                    </TableCell>
                    <TableCell sx={cellSx} align='center'>
                      <Stack direction='row' spacing={1} justifyContent='center' alignItems='center'>
                        <Tooltip title={`${images} photograph(s)`}>
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, fontSize: 11.5, color: images ? '#1d4ed8' : '#9ca3af' }}>
                            <ImageOutlinedIcon sx={{ fontSize: 15 }} />{images}
                          </Box>
                        </Tooltip>
                        {ev.video && <Tooltip title='Packing video attached'><VideocamOutlinedIcon sx={{ fontSize: 15, color: '#1d4ed8' }} /></Tooltip>}
                        {dws > 0 && <Chip size='small' label={`${dws} DWS`} sx={{ height: 18, fontSize: 10, bgcolor: '#f3f4f6' }} />}
                      </Stack>
                    </TableCell>
                    <TableCell sx={cellSx} align='center'>
                      <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{r.submissions || 0} / {WF_MAX_SUBMISSIONS}</Typography>
                      <Typography sx={muted}>round {r.round || 1}</Typography>
                    </TableCell>
                    <TableCell sx={cellSx}><WfStatusChip status={r.status} /></TableCell>
                    <TableCell sx={cellSx}>
                      <Typography sx={{ fontSize: 12 }}>{ageLabel(r.raisedAt)}</Typography>
                      <Typography sx={{ ...muted, fontSize: 10.5 }}>{fmtDate(r.raisedAt)}{r.raisedBy ? ` · ${r.raisedBy}` : ''}</Typography>
                    </TableCell>
                    <TableCell sx={cellSx} align='right'>
                      <Box component='button' type='button' onClick={(e) => { e.stopPropagation(); setOpenReq(r) }}
                        sx={{ fontSize: 11.5, fontWeight: 700, px: 1.5, py: 0.6, borderRadius: '6px', border: '1px solid #1565c0', color: '#1565c0', bgcolor: '#fff', cursor: 'pointer', whiteSpace: 'nowrap', '&:hover': { bgcolor: '#e3f2fd' } }}>
                        Review
                      </Box>
                    </TableCell>
                  </TableRow>
                )
              })}
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} sx={{ textAlign: 'center', py: 5 }}>
                    <AcUnitOutlinedIcon sx={{ fontSize: 28, color: '#cbd5e1' }} />
                    <Typography sx={{ fontSize: 12.5, color: '#6b7280', mt: 0.5 }}>No freeze requests in this tab for the current filters.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component='div'
          count={knownCount}
          page={page}
          onPageChange={(_e, p) => setPage(p)}
          rowsPerPage={size}
          onRowsPerPageChange={(e) => { setSize(Number(e.target.value)); setPage(0) }}
          rowsPerPageOptions={PAGE_SIZES}
          labelDisplayedRows={({ from, to }) => `${from}–${to}${hasMore ? ' of more' : ''}`}
          sx={{ '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select': { fontSize: 12 } }}
        />
      </Card>

      <FreezeRequestDrawer request={openReq} open={!!openReq} onClose={() => setOpenReq(null)} onChanged={onChanged} />
    </>
  )
}
